import { useState } from 'react'

import { Refresh } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useShowings } from '../../api/showings'
import { useCreateShowingSignup } from '../../api/showingSignup'
import { getCurrentLocale } from '../../helper'
import { HtmlConnected as Html } from '../html'

const formatRange = (locale, start, end) => {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).formatRange(new Date(start), new Date(end))
}

export const ShowingSignupForm = () => {
  const { t } = useTranslation()
  const { projectId } = useParams()

  const { data: showings = [], isLoading, refetch } = useShowings(projectId)

  const {
    mutate: createSignup,
    data: signupData,
    error,
    isPending: isSubmitting,
    isSuccess: signupSuccess,
  } = useCreateShowingSignup()

  // Field errors come from the response body; network failures only have a message.
  const signupError = error?.body || error

  const [state, setState] = useState({
    acceptPrivacyPolicy: false,
    acceptTracking: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    showingId: 0,
  })

  function handleChange(event) {
    setState({ ...state, [event.target.name]: event.target.value })
  }

  function handleCheck(event) {
    setState({ ...state, [event.target.name]: event.target.checked })
  }

  function handleClickReload() {
    refetch()
  }

  function handleSubmit(e) {
    e.preventDefault()
    const data = {
      acceptPrivacyPolicy: state?.acceptPrivacyPolicy,
      acceptTracking: state?.acceptTracking,
      firstName: state?.firstName,
      lastName: state?.lastName,
      email: state?.email,
      phone: state?.phone,
      showingId: state?.showingId,
      projectId,
    }
    createSignup(data)
  }

  if (signupSuccess) {
    const data = {
      ...state,
      ...signupData,
      showing: showings.find((s) => s.id == state.showingId),
    }
    // Add localized date range - prettyTime - to showing model
    data.showing.prettyTime = formatRange(
      getCurrentLocale()?.code,
      data.showing.startAt,
      data.showing.endAt
    )
    return <Html name="showing-signup-confirmation" data={data} />
  }

  return (
    <Stack component="form" onSubmit={handleSubmit} spacing={2}>
      <Html name="showing-signup-intro" />
      <Stack spacing={2}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t('First name')}
              name="firstName"
              value={state.firstName}
              onChange={handleChange}
              error={!!signupError?.firstName}
              helperText={signupError?.firstName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t('Last name')}
              name="lastName"
              value={state.lastName}
              onChange={handleChange}
              error={!!signupError?.lastName}
              helperText={signupError?.lastName}
            />
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              label={t('Email address')}
              name="email"
              type="email"
              value={state.email}
              onChange={handleChange}
              error={!!signupError?.email}
              helperText={signupError?.email}
            />
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              label={t('Phone number')}
              name="phone"
              type="tel"
              value={state.phone}
              onChange={handleChange}
              error={!!signupError?.phone}
              helperText={signupError?.phone}
            />
          </Grid2>
        </Grid2>
        <FormControl required error={!!signupError?.acceptPrivacyPolicy}>
          <FormControlLabel
            label={<Html name="privacy-policy-checkbox-label" />}
            control={
              <Checkbox
                name="acceptPrivacyPolicy"
                checked={state?.acceptPrivacyPolicy ? true : false}
                onChange={handleCheck}
              />
            }
          />
          <FormHelperText>{signupError?.acceptPrivacyPolicy}</FormHelperText>
        </FormControl>
      </Stack>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <h2>{t('Upcoming showings')}</h2>
          <Tooltip title={t('Reload the list')}>
            <IconButton onClick={handleClickReload} loading={isLoading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
        {!isLoading && showings?.length === 0 && <i>{t('No showings')}</i>}
        {!isLoading && showings?.length > 0 && (
          <RadioGroup
            name="showingId"
            value={state.showingId}
            onChange={handleChange}
          >
            {showings?.map?.((showing) => (
              <FormControlLabel
                key={showing.id}
                control={<Radio />}
                value={showing.id}
                label={
                  <Stack
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                    direction="row"
                    alignItems="center"
                  >
                    <Typography>{showing.description}</Typography>
                    <Chip
                      label={formatRange(
                        getCurrentLocale()?.code,
                        showing.startAt,
                        showing.endAt
                      )}
                    />
                  </Stack>
                }
                disabled={showing.isFull}
              />
            ))}
          </RadioGroup>
        )}
      </Stack>
      {signupError?.message && (
        <Alert severity="error">{signupError?.message}</Alert>
      )}
      <Box sx={{ pt: 3 }}>
        <Button variant="contained" type="submit" loading={isSubmitting}>
          {t('Send')}
        </Button>
      </Box>
    </Stack>
  )
}
